import { useState } from 'react';
import { Button, Chip, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { RoleFormDialog } from './RoleFormDialog';
import { rolesApi, type Role } from '../../api/roles.api';
import { extractApiError } from '../../api/error';

export function RoleListPage() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data: roles = [], isLoading } = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['roles'] });

  const createMut = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Parameters<typeof rolesApi.update>[1] }) => rolesApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });

  const columns: Column<Role>[] = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Name' },
    { key: 'isSystem', label: 'Type', render: (r) => <Chip size="small" label={r.isSystem ? 'System' : 'Custom'} color={r.isSystem ? 'default' : 'primary'} /> },
    { key: 'permissionIds', label: 'Permissions', render: (r) => r.permissionIds?.length ?? 0 },
  ];

  return (
    <>
      <PageHeader
        title="Roles & Permissions"
        action={<Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Role</Button>}
      />
      <DataTable
        columns={columns}
        rows={roles}
        loading={isLoading}
        page={1}
        limit={roles.length || 25}
        total={roles.length}
        onPageChange={() => undefined}
        onLimitChange={() => undefined}
        rowKey={(r) => r.id}
        actions={(r) => (
          <Tooltip title="Edit / permissions">
            <IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton>
          </Tooltip>
        )}
      />
      <RoleFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input: { name: input.name, description: input.description, permissionIds: input.permissionIds } });
          else await createMut.mutateAsync(input);
        }}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
